import { axiosInstance as axios } from '../../config/axios';
import { FACTURES_UNITATS } from '../../constants/roles';
import { CapNameInput } from './CapNameInput';
import { useFactures } from './useFacturesContext';
import {
	entryDisplayDate,
	fmtEntryAmount,
	fmtEUR,
	monthLabel,
	outstanding,
	type IEntry,
} from './facturesTypes';
import {
	Badge,
	EntryActions,
	EntryAmount,
	EntryConcept,
	EntryDate,
	EntryMain,
	EntryMeta,
	EntryRow,
	Panel,
	ReceiptLink,
	Row,
	SmallButton,
	SmallSaldo,
} from './Factures.styles';
import { Button, FormGroup, Input, Label } from '../Shared/FormStyles';
export const EntryItem = ({
	entry: e,
	saldo,
	showUnitat = false,
}: {
	entry: IEntry;
	/** null = hide the running-balance cell (global panel / drawer) */
	saldo: number | null;
	showUnitat?: boolean;
}) => {
	const fac = useFactures();
	const outstandingAmt = outstanding(e);

	if (fac.edit?.id === e._id) {
		const { edit } = fac;
		return (
			<Panel style={{ background: '#eef4ff' }}>
				<Row>
					<FormGroup>
						<Label>Import *</Label>
						<Input
							value={edit.amount}
							inputMode="decimal"
							onChange={(x) => fac.setEdit({ ...edit, amount: x.target.value })}
						/>
					</FormGroup>
					{fac.money && (
						<FormGroup>
							<Label>Part de la unitat</Label>
							<Input
								value={edit.unitatCharge}
								inputMode="decimal"
								onChange={(x) =>
									fac.setEdit({ ...edit, unitatCharge: x.target.value })
								}
							/>
						</FormGroup>
					)}
				</Row>
				<Row>
					<FormGroup>
						<Label>Concepte</Label>
						<Input
							value={edit.concept}
							onChange={(x) => fac.setEdit({ ...edit, concept: x.target.value })}
						/>
					</FormGroup>
					<FormGroup>
						<Label>Data despesa</Label>
						<Input
							type="date"
							value={edit.expenseDate}
							onChange={(x) =>
								fac.setEdit({ ...edit, expenseDate: x.target.value })
							}
						/>
					</FormGroup>
				</Row>
				<Row>
					<FormGroup>
						<Label>Nom del Cap</Label>
						<CapNameInput
							value={edit.capName}
							onChange={(v) => fac.setEdit({ ...edit, capName: v })}
							suggestions={fac.capNames}
						/>
					</FormGroup>
					{fac.money && (
						<FormGroup>
							<Label>Reemborsat fins ara (buit = pendent)</Label>
							<Input
								value={edit.reimbursed}
								inputMode="decimal"
								onChange={(x) =>
									fac.setEdit({ ...edit, reimbursed: x.target.value })
								}
							/>
						</FormGroup>
					)}
				</Row>
				{fac.money && (
					<Row>
						<FormGroup>
							<Label>Substituir factura</Label>
							<Input
								type="file"
								accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
								onChange={(x) =>
									fac.setEditReceipt((x.target.files as FileList)[0] ?? null)
								}
							/>
						</FormGroup>
					</Row>
				)}
				<Row>
					<Button onClick={fac.saveEdit} disabled={fac.submitting}>
						Guarda
					</Button>
					<Button onClick={() => fac.setEdit(null)}>Cancel·la</Button>
				</Row>
			</Panel>
		);
	}

	const reimbursed = e.reimbursedAmountCents != null && outstandingAmt === 0;
	return (
		<EntryRow $kind={e.type}>
			<EntryDate>{entryDisplayDate(e)}</EntryDate>
			<EntryMain>
				<EntryConcept>
					{e.type === 'mensual'
						? `Mensualitat de ${monthLabel(e.month ?? '')}`
						: e.type === 'ajust'
							? (e.note ?? 'Ajust')
							: e.concept}
					{e.type === 'ajust' && <Badge $tone="gray">ajust</Badge>}
					{showUnitat && (
						<Badge $tone="gray">
							{FACTURES_UNITATS.find((u) => u.id === e.unitat)?.nom ?? e.unitat}
						</Badge>
					)}
				</EntryConcept>
				<EntryMeta>
					{e.type === 'despesa' && e.capName && <>— {e.capName} </>}
					{e.type === 'despesa' &&
						(e.unitatChargeCents != null &&
						e.unitatChargeCents !== e.amountCents
							? ` — unitat: ${fmtEUR(e.unitatChargeCents)} / agrupament: ${fmtEUR(e.amountCents - e.unitatChargeCents)}`
							: '')}
					{e.type === 'despesa' && (
						<Badge $tone={reimbursed ? 'green' : 'red'}>
							{reimbursed
								? 'Reemborsada'
								: outstandingAmt < 0
									? `ha de tornar ${fmtEUR(-outstandingAmt)}`
									: `Pendent${outstandingAmt !== e.amountCents ? ` (falta ${fmtEUR(outstandingAmt)})` : ''}`}
						</Badge>
					)}
					{e.createdBy && <> · {e.createdBy}</>}
				</EntryMeta>
			</EntryMain>
			<EntryAmount $negative={fmtEntryAmount(e) < 0}>
				{fmtEUR(fmtEntryAmount(e))}
				{saldo != null && (
					<SmallSaldo>saldo: {fmtEUR(saldo)}</SmallSaldo>
				)}
			</EntryAmount>
			<EntryActions>
				{e.receipt && (
					<ReceiptLink
						href={`#factura-${e._id}`}
						onClick={async (ev: React.MouseEvent) => {
							ev.preventDefault();
							// Open synchronously so the browser keeps the user gesture;
							// async window.open gets blocked.
							const win = window.open('', '_blank');
							try {
								const res = await axios.get(
									`/api/finances/despesa/${e._id}/receipt-url`
								);
								if (win) win.location.href = res.data.url;
								else window.open(res.data.url, '_blank');
							} catch {
								win?.close();
								fac.setError("No s'ha pogut obrir la factura");
							}
						}}
					>
						factura
					</ReceiptLink>
				)}
				{fac.money && e.type === 'despesa' && outstandingAmt !== 0 && (
					<SmallButton onClick={() => fac.doReimburse(e._id)}>
						{outstandingAmt < 0 ? 'Salda' : 'Reembolsa'}
					</SmallButton>
				)}
				{fac.money && e.type === 'despesa' && (
					<SmallButton onClick={() => fac.doDespesaDelete(e._id)}>
						Esborra
					</SmallButton>
				)}
				{fac.money && e.type !== 'despesa' && (
					<SmallButton onClick={() => fac.doEntryDelete(e._id)}>
						Esborra
					</SmallButton>
				)}
				{fac.canWriteEntry(e) && e.type === 'despesa' && (
					<SmallButton onClick={() => fac.startEdit(e)}>Edita</SmallButton>
				)}
			</EntryActions>
		</EntryRow>
	);
};
